/**
 * Refresh Controls
 * Manual refresh button and optional auto-polling toggle
 */

import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { RefreshCw } from 'lucide-react';

interface RefreshControlsProps {
  onRefresh: () => void;
  isRefreshing: boolean;
  isPolling: boolean;
  onPollingChange: (enabled: boolean) => void;
  lastRefreshed?: Date;
}

export function RefreshControls({
  onRefresh,
  isRefreshing,
  isPolling,
  onPollingChange,
  lastRefreshed,
}: RefreshControlsProps) {
  const formatLastRefreshed = (date: Date) => {
    const now = new Date();
    const secondsAgo = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (secondsAgo < 10) return 'Just now';
    if (secondsAgo < 60) return `${secondsAgo}s ago`;
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m ago`;
    return date.toLocaleTimeString();
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>

        {lastRefreshed && (
          <span className="text-sm text-muted-foreground">
            Last updated: {formatLastRefreshed(lastRefreshed)}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="auto-polling"
          checked={isPolling}
          onCheckedChange={onPollingChange}
        />
        <Label htmlFor="auto-polling" className="text-sm cursor-pointer">
          Auto-refresh every 5 minutes
        </Label>
      </div>
    </div>
  );
}
